import React, { useState } from "react";
import { Outlet, Link, useLocation, NavLink } from "react-router-dom";
import { Zap, Phone, Menu, X } from "lucide-react";

const navItems = [
  { to: "/", label: "Accueil" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/#services", label: "Services", hash: true },
  { to: "/blog", label: "Journal" },
  { to: "/#contact", label: "Contact", hash: true },
];

export default function SiteLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header open={open} setOpen={setOpen} pathname={location.pathname} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function Header({ open, setOpen, pathname }) {
  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-md border-b border-border"
      data-testid="site-header"
    >
      <div className="container-page flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
          <div className="w-9 h-9 bg-graphite text-white flex items-center justify-center">
            <Zap size={18} className="copper" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[17px] tracking-tight">AMINE-ELEC</div>
            <div className="overline copper -mt-0.5">Bâtiment</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((n) =>
            n.hash ? (
              <a
                key={n.to}
                href={n.to}
                className="tech-link text-sm font-medium text-foreground/80 hover:text-foreground"
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </a>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `tech-link text-sm font-medium ${isActive ? "text-foreground" : "text-foreground/70 hover:text-foreground"}`
                }
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+33641287492"
            className="btn-copper text-sm"
            data-testid="header-cta-urgence"
          >
            <Phone size={15} strokeWidth={2.5} />
            Urgences 24/7
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 flex items-center justify-center border border-border"
          aria-label="Menu"
          data-testid="menu-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
          <div className="container-page py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <a
                key={n.to}
                href={n.hash ? n.to : n.to}
                onClick={() => setOpen(false)}
                className="text-base font-medium"
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </a>
            ))}
            <a
              href="tel:+33641287492"
              className="btn-copper text-sm justify-center mt-2"
              data-testid="mobile-cta-urgence"
            >
              <Phone size={15} strokeWidth={2.5} />
              Urgences 24/7
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-graphite text-white relative overflow-hidden" data-testid="site-footer">
      <div className="container-page py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 border border-white/30 flex items-center justify-center">
              <Zap size={20} className="copper" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-xl tracking-tight">AMINE-ELEC</div>
              <div className="overline text-white/60 -mt-0.5">Bâtiment · depuis 2013</div>
            </div>
          </div>
          <p className="text-white/70 max-w-md leading-relaxed">
            Électricien général Qualifelec. Installation, rénovation et dépannage pour particuliers
            et professionnels en Auvergne-Rhône-Alpes.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-white/50 mb-4">Navigation</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="hover:copper transition-colors">Accueil</Link></li>
            <li><Link to="/realisations" className="hover:copper transition-colors">Réalisations</Link></li>
            <li><Link to="/blog" className="hover:copper transition-colors">Journal</Link></li>
            <li><a href="/#contact" className="hover:copper transition-colors">Contact</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-white/50 mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li><a href="tel:+33641287492" className="hover:copper transition-colors">06 41 28 74 92</a></li>
            <li><a href="mailto:amine.elec15@gmail.com" className="hover:copper transition-colors">amine.elec15@gmail.com</a></li>
            <li className="text-white/70">Région Rhône-Alpes</li>
            <li className="text-white/70">Lun–Ven 8h–20h · Urgences 24/7</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-page py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} AMINE-ELEC · SIRET 534 194 857 00031</div>
          <div className="flex gap-6">
            <Link to="/admin/login" className="hover:text-white tech-link" data-testid="footer-admin-link">Espace pro</Link>
            <a href="#" className="hover:text-white tech-link">Mentions légales</a>
            <a href="#" className="hover:text-white tech-link">Confidentialité</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
